/**
 * A hub's incident log, in its own dialog off the hub card: what happened to it in the
 * field (a flooded box, a stolen panel, a modem swapped) and on which day. The
 * monitoring grid shows when a hub went quiet; this is where we write down why.
 *
 * Stored in the database only — unlike the hub settings, none of this reaches
 * the hub. Days are UTC, like every other date on these pages.
 */
import { useState } from 'react';
import { Loader2Icon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
    HubIncident,
    HubIncidentInput,
    useCreateHubIncident,
    useDeleteHubIncident,
    useHubIncidents,
    useUpdateHubIncident,
} from '@/services/hub.service';
import { saveErrorMessage } from './saveError';

const FIELD_CLASS =
    'px-2 py-1 rounded text-sm bg-[#0f141a] border border-[#2d2d2d] text-[#d8dde2] placeholder:text-[#6b7480] focus:outline-none focus:border-[#74c69d] [color-scheme:dark]';

/** Today as `YYYY-MM-DD`, in UTC. */
function todayUtc(): string {
    return new Date().toISOString().slice(0, 10);
}

export default function HubIncidents({ macAddress }: { macAddress: string }) {
    const { data: incidents, isLoading, error } = useHubIncidents(macAddress);
    const create = useCreateHubIncident();
    const update = useUpdateHubIncident();
    const remove = useDeleteHubIncident();
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);

    if (isLoading) return <Skeleton className="h-10 w-full bg-[#1e242a]" />;
    if (error)
        return <div className="text-red-400 text-sm">{error.toString()}</div>;

    const onDelete = (incident: HubIncident) => {
        if (!window.confirm(`Delete the incident "${incident.name}"?`)) return;
        remove.mutate({ macAddress, id: incident.id });
    };

    return (
        <div className="flex flex-col gap-3">
            {adding ? (
                <IncidentForm
                    initial={{ name: '', occurredAt: todayUtc(), description: '' }}
                    pending={create.isPending}
                    error={create.error}
                    onCancel={() => {
                        create.reset();
                        setAdding(false);
                    }}
                    onSubmit={(incident) =>
                        create.mutate(
                            { macAddress, incident },
                            { onSuccess: () => setAdding(false) },
                        )
                    }
                />
            ) : (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        setEditingId(null);
                        setAdding(true);
                    }}
                    className="h-7 w-fit bg-[#0f141a] border-[#1e242a] text-[#d8dde2] hover:bg-[#1a2126] hover:text-white"
                >
                    <PlusIcon className="h-3.5 w-3.5 mr-1.5" />
                    Add incident
                </Button>
            )}

            {remove.isError && (
                <div
                    className="text-[10px] text-[#d9534f]"
                    title={saveErrorMessage(remove.error)}
                >
                    not deleted
                </div>
            )}

            {incidents && incidents.length === 0 && !adding && (
                <div className="text-xs text-[#6b7480]">
                    No incident recorded for this hub.
                </div>
            )}

            <ul className="flex flex-col gap-2">
                {incidents?.map((incident) =>
                    editingId === incident.id ? (
                        <li key={incident.id}>
                            <IncidentForm
                                initial={{
                                    name: incident.name,
                                    occurredAt: incident.occurredAt.slice(0, 10),
                                    description: incident.description,
                                }}
                                pending={update.isPending}
                                error={update.error}
                                onCancel={() => {
                                    update.reset();
                                    setEditingId(null);
                                }}
                                onSubmit={(values) =>
                                    update.mutate(
                                        {
                                            macAddress,
                                            id: incident.id,
                                            incident: values,
                                        },
                                        { onSuccess: () => setEditingId(null) },
                                    )
                                }
                            />
                        </li>
                    ) : (
                        <li
                            key={incident.id}
                            className="group rounded border border-[#1e242a] bg-[#0f141a] px-3 py-2"
                        >
                            <div className="flex items-baseline gap-2">
                                <span className="font-mono text-xs text-[#a8b0bb] tabular-nums flex-shrink-0">
                                    {incident.occurredAt.slice(0, 10)}
                                </span>
                                <span className="text-sm font-semibold text-white break-words min-w-0">
                                    {incident.name}
                                </span>
                                <div className="ml-auto flex gap-1 flex-shrink-0">
                                    <IconButton
                                        title="Edit"
                                        onClick={() => {
                                            setAdding(false);
                                            setEditingId(incident.id);
                                        }}
                                    >
                                        <PencilIcon className="h-3 w-3" />
                                    </IconButton>
                                    <IconButton
                                        title="Delete"
                                        disabled={remove.isPending}
                                        onClick={() => onDelete(incident)}
                                    >
                                        <Trash2Icon className="h-3 w-3" />
                                    </IconButton>
                                </div>
                            </div>
                            {incident.description && (
                                <p className="mt-1 text-xs text-[#a8b0bb] whitespace-pre-wrap break-words">
                                    {incident.description}
                                </p>
                            )}
                        </li>
                    ),
                )}
            </ul>
        </div>
    );
}

function IncidentForm({
    initial,
    pending,
    error,
    onSubmit,
    onCancel,
}: {
    initial: HubIncidentInput;
    pending: boolean;
    error: unknown;
    onSubmit: (incident: HubIncidentInput) => void;
    onCancel: () => void;
}) {
    const [name, setName] = useState(initial.name);
    const [occurredAt, setOccurredAt] = useState(initial.occurredAt);
    const [description, setDescription] = useState(initial.description);
    const valid = name.trim().length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(occurredAt);

    return (
        <form
            className="flex flex-col gap-2 rounded border border-[#2d2d2d] bg-[#0f141a] p-3"
            onSubmit={(event) => {
                event.preventDefault();
                if (valid) onSubmit({ name, occurredAt, description });
            }}
        >
            <div className="flex gap-2">
                <input
                    autoFocus
                    type="text"
                    value={name}
                    maxLength={200}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Name"
                    className={`${FIELD_CLASS} flex-1 min-w-0`}
                />
                <input
                    type="date"
                    value={occurredAt}
                    onChange={(event) => setOccurredAt(event.target.value)}
                    title="Day of the incident (UTC)"
                    className={`${FIELD_CLASS} w-44 flex-shrink-0`}
                />
            </div>
            <textarea
                value={description}
                maxLength={5000}
                rows={3}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Description"
                className={`${FIELD_CLASS} w-full resize-y`}
            />
            <div className="flex items-center gap-2">
                {!!error && (
                    <span
                        className="text-[10px] text-[#d9534f] truncate"
                        title={saveErrorMessage(error)}
                    >
                        not saved — {saveErrorMessage(error)}
                    </span>
                )}
                <div className="ml-auto flex gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onCancel}
                        className="h-7 text-[#a8b0bb] hover:bg-[#1a2126] hover:text-white"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        size="sm"
                        disabled={!valid || pending}
                        variant="outline"
                        className="h-7 bg-[#0f141a] border border-[#1e242a] text-[#d8dde2] hover:bg-[#1a2126] hover:text-white"
                    >
                        {pending && (
                            <Loader2Icon className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        )}
                        Save
                    </Button>
                </div>
            </div>
        </form>
    );
}

function IconButton({
    title,
    onClick,
    disabled,
    children,
}: {
    title: string;
    onClick: () => void;
    disabled?: boolean;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            title={title}
            disabled={disabled}
            onClick={onClick}
            className="h-5 w-5 flex items-center justify-center rounded text-[#6b7480] hover:text-white hover:bg-[#1a2126] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#6b7480]"
        >
            {children}
        </button>
    );
}
